import type { ComponentType } from "react";
import {
    LuMusic,
    LuUtensils,
    LuPalette,
    LuBriefcase,
    LuLaptop,
    LuSmile,
    LuBookOpen,
    LuUsers,
    LuShirt,
    LuFilm,
    LuDumbbell,
    LuSparkles,
} from "react-icons/lu";

type CategoryTheme = {
    gradient: string;
    Icon: ComponentType<{ className?: string; "aria-hidden"?: boolean }>;
    label: string;
};

const THEMES: Record<string, CategoryTheme> = {
    music: {
        gradient: "from-violet-500/35 via-fuchsia-500/15 to-black",
        Icon: LuMusic,
        label: "Music",
    },
    food: {
        gradient: "from-amber-500/35 via-rose-500/15 to-black",
        Icon: LuUtensils,
        label: "Food",
    },
    arts: {
        gradient: "from-pink-500/35 via-rose-500/15 to-black",
        Icon: LuPalette,
        label: "Arts",
    },
    business: {
        gradient: "from-slate-500/35 via-indigo-500/20 to-black",
        Icon: LuBriefcase,
        label: "Business",
    },
    tech: {
        gradient: "from-cyan-500/35 via-blue-500/15 to-black",
        Icon: LuLaptop,
        label: "Tech",
    },
    comedy: {
        gradient: "from-yellow-400/35 via-orange-500/15 to-black",
        Icon: LuSmile,
        label: "Comedy",
    },
    education: {
        gradient: "from-emerald-500/35 via-teal-500/15 to-black",
        Icon: LuBookOpen,
        label: "Education",
    },
    family: {
        gradient: "from-sky-500/35 via-indigo-500/15 to-black",
        Icon: LuUsers,
        label: "Family",
    },
    fashion: {
        gradient: "from-pink-500/35 via-purple-500/15 to-black",
        Icon: LuShirt,
        label: "Fashion",
    },
    film: {
        gradient: "from-slate-500/35 via-purple-500/20 to-black",
        Icon: LuFilm,
        label: "Film",
    },
    fitness: {
        gradient: "from-lime-500/35 via-emerald-500/15 to-black",
        Icon: LuDumbbell,
        label: "Fitness",
    },
};

const DEFAULT_THEME: CategoryTheme = {
    gradient: "from-[rgba(58,92,255,0.35)] via-[rgba(23,21,104,0.25)] to-black",
    Icon: LuSparkles,
    label: "Event",
};

const slug = (input: string) =>
    input.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

function resolveTheme(input?: string | null): CategoryTheme {
    if (!input) return DEFAULT_THEME;
    const s = slug(input);
    if (s.includes("music") || s.includes("dj") || s.includes("concert")) return THEMES.music;
    if (s.includes("food") || s.includes("drink") || s.includes("dining")) return THEMES.food;
    if (s.includes("art") || s.includes("culture") || s.includes("exhibit")) return THEMES.arts;
    if (s.includes("business") || s.includes("network") || s.includes("startup") || s.includes("meetup"))
        return THEMES.business;
    if (s.includes("tech") || s.includes("dev") || s.includes("coding")) return THEMES.tech;
    if (s.includes("comedy") || s.includes("stand up")) return THEMES.comedy;
    if (s.includes("education") || s.includes("workshop") || s.includes("class") || s.includes("masterclass"))
        return THEMES.education;
    if (s.includes("family") || s.includes("kids")) return THEMES.family;
    if (s.includes("fashion")) return THEMES.fashion;
    if (s.includes("film") || s.includes("media") || s.includes("movie")) return THEMES.film;
    if (s.includes("fitness") || s.includes("sport") || s.includes("yoga") || s.includes("run"))
        return THEMES.fitness;
    return DEFAULT_THEME;
}

interface Props {
    title: string;
    category?: string | null;
    size?: "sm" | "md";
}

export function EventPlaceholderCover({ title, category, size = "md" }: Props) {
    const theme = resolveTheme(category) ?? resolveTheme(title);
    const Icon = theme.Icon;
    const iconClass =
        size === "sm" ? "h-6 w-6" : "h-8 w-8 md:h-10 md:w-10";
    const labelClass =
        size === "sm"
            ? "text-[9px] tracking-[0.28em]"
            : "text-[10px] tracking-[0.3em] md:text-[11px]";
    return (
        <div className={`relative h-full w-full overflow-hidden bg-gradient-to-br ${theme.gradient}`}>
            {/* subtle noise */}
            <div
                className="pointer-events-none absolute inset-0 opacity-[0.06]"
                style={{
                    backgroundImage:
                        "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
                }}
            />
            {/* soft highlight */}
            <div
                className="pointer-events-none absolute inset-0"
                style={{
                    background:
                        "radial-gradient(ellipse 55% 50% at 50% 40%, rgba(255,255,255,0.10), transparent 70%)",
                }}
            />
            <div className="relative flex h-full w-full flex-col items-center justify-center gap-2 px-3 text-center">
                <Icon className={`text-white/75 ${iconClass}`} aria-hidden />
                <span className={`font-semibold uppercase text-white/70 ${labelClass}`}>
                    {theme.label}
                </span>
            </div>
        </div>
    );
}
